import React, { useState } from 'react'
import FileSelectButton from '../../components/shared/FileSelectButton'
import CameraCaptureButton from '../../components/shared/CameraCaptureButton'
import MultiImageCapture from '../../components/shared/MultiImageCapture'
import MultiFileSelectButton from '../../components/shared/MultiFileSelectButton'
import ExtractButton from '../../components/shared/ExtractButton'
import FilePreview from '../../components/shared/FilePreview'
import EinzelfotoThumbnail from '../../components/shared/EinzelfotoThumbnail'
import RechnungFieldsView from './RechnungFieldsView'
import RechnungTabelle from './RechnungTabelle'
import LoadingSpinner from '../../components/shared/LoadingSpinner'
import useDokumentExtraktion from '../../components/shared/useDokumentExtraktion'
import useDokumentListe from '../../components/shared/useDokumentListe'
import useStapelverarbeitung from '../../components/shared/useStapelverarbeitung'
import useIsDesktop from '../../components/shared/useIsDesktop'
import useKameraErzwingen from '../../components/shared/useKameraErzwingen'
import { useMobilAnsichtErzwingen } from '../../components/shared/useMobilAnsichtErzwingen'
import { useDialog } from '../../components/shared/Dialog'
import { exportiereRechnungen, sendeRechnungenPerEmail } from './export'
import { mitIbanDefault, mitKommentarDefault } from './rechnungAnsicht'
import EmailExportDialog from './EmailExportDialog'
import { useAuth } from '../../authContext'

// Fest verdrahteter Prompt-Key für Rechnungen (GET /prompts/Eingangsrechnung_Optimiert) - jede
// Dokumentklasse verdrahtet hier ihren eigenen Key, der generische Hook kennt ihn nicht.
const PROMPT_KEY = 'Eingangsrechnung_Optimiert'

// Das Häkchen "📷 Aufnahme-Buttons anzeigen" ist seit Einführung der global erzwingbaren
// Mobil-Ansicht (Header-Schalter "📱 Mobile Ansicht", siehe useMobilAnsichtErzwingen) redundant
// geworden - beide lösten dasselbe Problem (hochauflösende Tablets, die als Desktop erkannt
// werden). Bewusst nur ausgeblendet, nicht entfernt, falls sich das nochmal ändern sollte.
const KAMERA_TOGGLE_SICHTBAR = false

// mwst_betrag_eur ist ein verschachteltes Objekt - beim Start der Bearbeitung wird eine flache
// Kopie der Rechnung (inkl. eigener Kopie von mwst_betrag_eur) als Entwurf angelegt, damit
// Änderungen am Entwurf nicht versehentlich das Original im Listen-Eintrag mit-mutieren.
function kopiereRechnung (rechnung) {
  if (!rechnung) return null
  return {
    ...rechnung,
    mwst_betrag_eur: { ...(rechnung.mwst_betrag_eur || {}) }
  }
}

function RechnungTab () {
  const {
    file,
    images,
    selectFile,
    selectImages,
    extract,
    loading,
    error,
    dokument: rechnung,
    setDokument: setRechnung,
    validation,
    reset
  } = useDokumentExtraktion(PROMPT_KEY)
  const rechnungListe = useDokumentListe()
  const { confirm, alert } = useDialog()
  const {
    stapelLaeuft,
    fortschritt,
    starteStapel,
    abbrechen: stapelAbbrechen
  } = useStapelverarbeitung(PROMPT_KEY, {
    // Jedes Ergebnis (auch Fehlerzeilen) wird sofort der Tabelle hinzugefügt - der Benutzer
    // kann während des laufenden Stapellaufs zusehen und danach nachbearbeiten.
    // mitIbanDefault() sorgt dafür, dass auch Einträge, die nie über das Formular liefen (der
    // Regelfall bei der Stapelverarbeitung), sofort eine Default-IBAN-Auswahl bekommen - sonst
    // gäbe es dafür nie eine Gelb-Markierung in der Tabelle, da ibanAuswahlOffen sonst auf
    // undefined-Feldern rechnet.
    onErgebnis: eintrag =>
      rechnungListe.hinzufuegen({
        ...eintrag,
        dokument: mitKommentarDefault(
          mitIbanDefault(eintrag.dokument),
          eintrag.validation
        )
      }),
    // Dokumenten-Kontingent (Rolle "Tester") mitten im Stapellauf aufgebraucht - Lauf wurde
    // bereits abgebrochen (siehe useStapelverarbeitung), hier nur noch die Meldung anzeigen.
    onKontingentAufgebraucht: ({ verarbeitet, gesamt }) => {
      alert({
        titel: 'Stapelverarbeitung abgebrochen',
        nachricht: `Nach ${verarbeitet} von ${gesamt} Dokumenten abgebrochen: Das Dokumenten-Kontingent ist aufgebraucht.`
      })
    }
  })
  const isDesktop = useIsDesktop()
  const [kameraErzwingen, setKameraErzwingen] = useKameraErzwingen()
  const [mobilAnsichtErzwingen] = useMobilAnsichtErzwingen()
  // Effektiv "Kamera zeigen": echtes Mobilgerät (per Breakpoint erkannt) ODER vom Nutzer
  // manuell übersteuert (z.B. hochauflösendes Tablet, das fälschlich als Desktop gilt) ODER
  // die global erzwungene Mobil-Ansicht ist aktiv (dann macht i.d.R. auch die Kameraaufnahme Sinn).
  const kameraVerfuegbar =
    !isDesktop || kameraErzwingen || mobilAnsichtErzwingen
  const { token, dokumentKontingent, dokumentVerbraucht } = useAuth()
  // null = unbegrenzt (Admin/User), sonst Rolle "Tester" mit Mengenbegrenzung.
  const kontingentVerbleibend =
    dokumentKontingent != null
      ? Math.max(0, dokumentKontingent - dokumentVerbraucht)
      : null
  const [multiPhotoMode, setMultiPhotoMode] = useState(false)
  const [multiPhotoResetSignal, setMultiPhotoResetSignal] = useState(0)

  const handleKameraErzwingenChange = wert => {
    setKameraErzwingen(wert)
    // Kamera-/Desktop-Untermodus der Mehrseiten-Erfassung wechselt dadurch - eine ggf. schon
    // offene Erfassung (mit bereits gesammelten Fotos) würde sonst einfach im jeweils anderen
    // Untermodus hängen bleiben, statt zu verschwinden. Deshalb hier schließen und zurücksetzen,
    // genau wie bei "neue Datei ausgewählt"/"neue Mehrseiten-Aufnahme begonnen".
    setMultiPhotoMode(false)
    setMultiPhotoResetSignal(n => n + 1)
  }

  // null = Neu-Erfassen, sonst id des gerade bearbeiteten Listen-Eintrags.
  const [bearbeitungsId, setBearbeitungsId] = useState(null)
  // Entwurf der Rechnung während der Bearbeitung - noch NICHT in der Liste gespeichert,
  // erst bei "Speichern" wird er per rechnungListe.aktualisieren übernommen. So bleibt ein
  // echtes "Abbrechen" möglich (Entwurf wird verworfen, der Listen-Eintrag bleibt unverändert).
  const [entwurf, setEntwurf] = useState(null)

  const bearbeiteterEintrag = bearbeitungsId
    ? rechnungListe.eintraege.find(e => e.id === bearbeitungsId)
    : null

  // Anzeige-Werte je nach Modus: im Bearbeiten-Modus aus dem Entwurf (Datei/Validation kommen
  // unverändert aus dem Listen-Eintrag), sonst aus der laufenden Neu-Erfassung (useExtraction).
  const angezeigteDatei = bearbeitungsId
    ? bearbeiteterEintrag?.file ?? null
    : file
  const angezeigteRechnung = mitKommentarDefault(
    mitIbanDefault(bearbeitungsId ? entwurf : rechnung),
    bearbeitungsId ? bearbeiteterEintrag?.validation ?? null : validation
  )
  const angezeigteValidation = bearbeitungsId
    ? bearbeiteterEintrag?.validation ?? null
    : validation

  const zeileBearbeiten = id => {
    reset() // laufende, nicht gespeicherte Neu-Erfassung verwerfen
    const eintrag = rechnungListe.eintraege.find(e => e.id === id)
    setEntwurf(kopiereRechnung(eintrag?.dokument))
    setBearbeitungsId(id)
  }

  const beendeBearbeitung = () => {
    setBearbeitungsId(null)
    setEntwurf(null)
    reset()
  }

  const rechnungAendern = neueRechnung => {
    if (bearbeitungsId) {
      setEntwurf(neueRechnung)
    } else {
      setRechnung(neueRechnung)
    }
  }

  const kannBestaetigen = !loading && !!angezeigteRechnung

  const bestaetigen = () => {
    if (bearbeitungsId) {
      const validationFuerEntwurf = bearbeiteterEintrag?.validation ?? null
      rechnungListe.aktualisieren(bearbeitungsId, {
        dokument: mitKommentarDefault(
          mitIbanDefault(entwurf),
          validationFuerEntwurf
        )
      })
      beendeBearbeitung()
    } else {
      rechnungListe.hinzufuegen({
        file,
        dokument: mitKommentarDefault(mitIbanDefault(rechnung), validation),
        validation
      })
      reset()
    }
  }

  const abbrechen = () => {
    beendeBearbeitung() // Entwurf wird verworfen, nichts wird in die Liste geschrieben
  }

  const zeileLoeschen = id => {
    rechnungListe.entfernen(id)
    if (id === bearbeitungsId) beendeBearbeitung()
  }

  const alleZeilenLoeschen = async () => {
    const bestaetigt = await confirm({
      titel: 'Alle Einträge löschen',
      nachricht: `Wirklich alle ${rechnungListe.eintraege.length} Einträge aus der Tabelle löschen? Das kann nicht rückgängig gemacht werden.`,
      bestaetigenLabel: 'Alle löschen',
      gefahr: true
    })
    if (bestaetigt) {
      rechnungListe.alleEntfernen()
      beendeBearbeitung() // eine ggf. offene Bearbeitung wird ebenfalls verworfen
    }
  }

  const [exportLoading, setExportLoading] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [emailDialogOffen, setEmailDialogOffen] = useState(false)

  const handleExport = async () => {
    setExportLoading(true)
    setExportError(null)
    try {
      await exportiereRechnungen(rechnungListe.eintraege, token)
    } catch (err) {
      setExportError(
        'Export fehlgeschlagen: ' + (err.message || 'Unbekannter Fehler')
      )
    } finally {
      setExportLoading(false)
    }
  }

  const handleEmailErfolg = () => {
    setEmailDialogOffen(false)
    alert({
      titel: 'Versendet',
      nachricht: 'E-Mail wurde gesendet – Überprüfen Sie den Empfang'
    })
  }

  // Dateien aus der Mehrfachauswahl, die auf den Klick auf "Extrahieren" warten - der
  // Stapellauf selbst startet NICHT automatisch bei der Auswahl (siehe handleExtrahierenKlick).
  const [stapelDateien, setStapelDateien] = useState([])

  const handleSingleFileSelected = selectedFile => {
    setMultiPhotoMode(false)
    setMultiPhotoResetSignal(n => n + 1)
    setStapelDateien([]) // Einzelauswahl und Stapelauswahl schließen sich gegenseitig aus
    selectFile(selectedFile)
  }

  const handleToggleMultiPhoto = () => {
    setStapelDateien([])
    setMultiPhotoMode(prev => {
      const next = !prev
      if (next) setMultiPhotoResetSignal(n => n + 1)
      return next
    })
  }

  const handleImagesReady = files => {
    setStapelDateien([])
    selectImages(files)
    setMultiPhotoMode(false)
  }

  const handleMehrereDateienAusgewaehlt = dateien => {
    setMultiPhotoMode(false)
    reset()
    setStapelDateien(dateien)
  }

  // Ein Klick auf "Extrahieren" startet je nach aktueller Auswahl entweder die Einzelextraktion
  // oder den Stapellauf. Reicht das Kontingent (Rolle "Tester") absehbar nicht für alle
  // ausgewählten Dateien, wird vorab gewarnt - der Lauf bricht serverseitig ohnehin an der
  // richtigen Stelle ab (siehe useStapelverarbeitung), diese Warnung ist reiner Komfort.
  // Nach Ende des Stapellaufs (fertig oder abgebrochen) wird die Stapelauswahl geleert, da alle
  // Ergebnisse bereits in der Tabelle stehen.
  const handleExtrahierenKlick = async () => {
    if (stapelDateien.length > 0) {
      if (
        kontingentVerbleibend != null &&
        stapelDateien.length > kontingentVerbleibend
      ) {
        const bestaetigt = await confirm({
          titel: 'Kontingent reicht nicht für alle Dateien',
          nachricht:
            kontingentVerbleibend === 0
              ? 'Das Dokumenten-Kontingent ist bereits aufgebraucht. Keine der ausgewählten Dateien kann verarbeitet werden.'
              : `Nur noch ${kontingentVerbleibend} von ${stapelDateien.length} ausgewählten Dokumenten können verarbeitet werden. Trotzdem starten?`,
          bestaetigenLabel: 'Trotzdem starten',
          abbrechenLabel: 'Abbrechen'
        })
        if (!bestaetigt) return
      }
      await starteStapel(stapelDateien)
      setStapelDateien([])
    } else {
      extract()
    }
  }

  const hasSelection =
    !!file || (images && images.length > 0) || stapelDateien.length > 0
  const erfassungGesperrt =
    loading ||
    bearbeitungsId !== null ||
    stapelLaeuft ||
    kontingentVerbleibend === 0
  // Mobilansicht: die große Vorschauspalte wird NIE angezeigt (unabhängig davon, ob gerade
  // eine Datei ausgewählt ist) - der Benutzer hat das Dokument beim Fotografieren ohnehin
  // physisch vor sich, sie nimmt nur Platz weg. Getrennt davon: das kleine Einzelfoto-Thumbnail
  // (an der Stelle, an der bei mehrseitigen Aufnahmen die Thumbnail-Reihe erscheint) braucht
  // zusätzlich tatsächlich eine ausgewählte Datei, sonst gibt es nichts anzuzeigen.
  const zeigeGrosseVorschauSpalte = !kameraVerfuegbar
  const zeigeEinzelfotoThumbnail =
    kameraVerfuegbar && !multiPhotoMode && !!angezeigteDatei

  return (
    <div className='tab-content'>
      <div className='extraction-layout'>
        {/* Linke Spalte: nur die Vorschau, füllt die gesamte Spalte aus - in der Mobilansicht
            grundsätzlich ausgeblendet (siehe zeigeGrosseVorschauSpalte) */}
        {zeigeGrosseVorschauSpalte && (
          <div className='extraction-vorschau-spalte'>
            <div className='vorschau-mit-overlay'>
              <FilePreview file={angezeigteDatei} />
              {loading && (
                <div className='vorschau-overlay'>
                  <LoadingSpinner label='Extrahiere…' />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rechte Spalte: oben Tabelle (70%), unten Steuerung + Formular (30%) */}
        <div className='extraction-arbeitsbereich'>
          <div className='extraction-tabelle-bereich'>
            <div className='rechnung-tabelle-kopf'>
              <h3>Erfasste Rechnungen ({rechnungListe.eintraege.length})</h3>
              <div className='steuerung-rechts'>
                <button
                  type='button'
                  className='btn btn-mobil btn-mobil-breite-a btn-desktop btn-desktop-kurz'
                  onClick={alleZeilenLoeschen}
                  disabled={rechnungListe.eintraege.length === 0}
                >
                  🗑 Alle löschen
                </button>
                <button
                  type='button'
                  className='btn btn-mobil btn-mobil-breite-a btn-desktop btn-desktop-lang'
                  onClick={() => setEmailDialogOffen(true)}
                  disabled={rechnungListe.eintraege.length === 0}
                >
                  📧 Export als Mail
                </button>
                <button
                  type='button'
                  className='btn btn-primary btn-mobil btn-mobil-breite-a btn-desktop btn-desktop-kurz'
                  onClick={handleExport}
                  disabled={
                    rechnungListe.eintraege.length === 0 || exportLoading
                  }
                >
                  {exportLoading ? 'Exportiere…' : '⬇ Export'}
                </button>
              </div>
            </div>
            {exportError && <div className='error-banner'>{exportError}</div>}
            {emailDialogOffen && (
              <EmailExportDialog
                onSenden={adresse =>
                  sendeRechnungenPerEmail(
                    rechnungListe.eintraege,
                    adresse,
                    token
                  )
                }
                onErfolg={handleEmailErfolg}
                onAbbrechen={() => setEmailDialogOffen(false)}
              />
            )}
            <RechnungTabelle
              eintraege={rechnungListe.eintraege}
              bearbeitungsId={bearbeitungsId}
              onZeileBearbeiten={zeileBearbeiten}
              onZeileLoeschen={zeileLoeschen}
            />
          </div>

          <div className='extraction-formular-bereich'>
            {kameraVerfuegbar ? (
              /* ===== Mobil-Ansicht: unverändert, eine Zeile, Flexbox ===== */
              <div className='extraction-steuerung-zeile'>
                <div className='steuerung-links'>
                  <CameraCaptureButton
                    onFileSelected={handleSingleFileSelected}
                    disabled={erfassungGesperrt}
                    className='btn-mobil btn-mobil-breite-b'
                  />
                  <button
                    type='button'
                    className='btn btn-mobil btn-mobil-breite-b'
                    onClick={handleToggleMultiPhoto}
                    disabled={erfassungGesperrt}
                  >
                    📑 Mehrseitig aufnehmen
                  </button>
                </div>
                <div className='steuerung-rechts'>
                  <div className='kontingent-extrahieren-gruppe'>
                    {kontingentVerbleibend !== null && (
                      <span
                        className={`kontingent-anzeige${
                          kontingentVerbleibend === 0
                            ? ' kontingent-aufgebraucht'
                            : ''
                        }`}
                      >
                        Noch {kontingentVerbleibend} von {dokumentKontingent}{' '}
                        Dokumenten
                      </span>
                    )}
                    <ExtractButton
                      onExtract={handleExtrahierenKlick}
                      disabled={!hasSelection || erfassungGesperrt}
                      loading={loading || stapelLaeuft}
                      className='btn-mobil btn-mobil-breite-c'
                    />
                  </div>
                  <button
                    type='button'
                    className='btn btn-primary btn-mobil btn-mobil-breite-c'
                    onClick={bestaetigen}
                    disabled={!kannBestaetigen}
                  >
                    {bearbeitungsId ? '✔ Speichern' : '✔ Übernehmen'}
                  </button>
                  {bearbeitungsId && (
                    <button
                      type='button'
                      className='btn btn-mobil'
                      onClick={abbrechen}
                    >
                      Abbrechen
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* ===== Desktop-Ansicht: CSS Grid über zwei Zeilen, feste Positionen =====
                 Nur "Mehrere Rechnungen auswählen" und "Rechnung auswählen" (Zeile 1) sowie
                 "Mehrere Bilder auswählen" (Zeile 2) bekommen eigene, feste Grid-Spalten - das
                 sind die Buttons, die nie springen dürfen. Status-Text + "Stopp" (während eines
                 Stapellaufs) + die rechte Aktionsgruppe (Extrahieren/Übernehmen/Abbrechen) teilen
                 sich eine gemeinsame, flexible Zelle (.grid-rest): der Status-Text darf bei
                 Platzmangel schrumpfen/abschneiden, die Aktionsgruppe bleibt dabei als Ganzes
                 rechtsbündig zusammen und bricht nicht mehr um. */
              <div className='steuerung-grid-wrapper'>
                <div className='extraction-steuerung-zeile'>
                  <div className='steuerung-links'>
                    <MultiFileSelectButton
                      onFilesSelected={handleMehrereDateienAusgewaehlt}
                      disabled={erfassungGesperrt}
                      className='btn-desktop btn-desktop-lang grid-mehr-rechnungen'
                    />
                    <FileSelectButton
                      onFileSelected={handleSingleFileSelected}
                      disabled={erfassungGesperrt}
                      className='btn-desktop btn-desktop-lang grid-rechnung-auswaehlen'
                    />
                    {KAMERA_TOGGLE_SICHTBAR && isDesktop && (
                      <label className='kamera-toggle'>
                        <input
                          type='checkbox'
                          checked={kameraErzwingen}
                          onChange={e =>
                            handleKameraErzwingenChange(e.target.checked)
                          }
                        />
                        📷 Aufnahme-Buttons anzeigen
                      </label>
                    )}
                  </div>

                  <div className='grid-rest'>
                    <div className='grid-rest-status'>
                      {stapelLaeuft && (
                        <button
                          type='button'
                          className='btn btn-desktop'
                          onClick={stapelAbbrechen}
                        >
                          ⏹ Stapellauf abbrechen
                        </button>
                      )}
                      <span className='filename'>
                        <strong>
                          {stapelLaeuft && fortschritt
                            ? `${fortschritt.aktuell} von ${fortschritt.gesamt} Dokumenten…`
                            : stapelDateien.length > 0
                            ? `${stapelDateien.length} ${
                                stapelDateien.length === 1 ? 'Datei' : 'Dateien'
                              } ausgewählt`
                            : angezeigteDatei
                            ? angezeigteDatei.name
                            : images?.length
                            ? `${images.length} Bild(er) ausgewählt`
                            : 'Keine Datei ausgewählt'}
                        </strong>
                      </span>
                    </div>

                    <div className='steuerung-rechts'>
                      <ExtractButton
                        onExtract={handleExtrahierenKlick}
                        disabled={!hasSelection || erfassungGesperrt}
                        loading={loading || stapelLaeuft}
                        className='btn-desktop btn-desktop-kurz'
                      />
                      <button
                        type='button'
                        className='btn btn-primary btn-desktop btn-desktop-kurz'
                        onClick={bestaetigen}
                        disabled={!kannBestaetigen}
                      >
                        {bearbeitungsId ? '✔ Speichern' : '✔ Übernehmen'}
                      </button>
                      {bearbeitungsId && (
                        <button
                          type='button'
                          className='btn btn-desktop btn-desktop-kurz'
                          onClick={abbrechen}
                        >
                          Abbrechen
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className='extraction-steuerung-zeile stapel-steuerung'>
                  <div className='steuerung-links'>
                    <button
                      type='button'
                      className='btn btn-desktop btn-desktop-lang grid-mehr-bilder'
                      onClick={handleToggleMultiPhoto}
                      disabled={erfassungGesperrt}
                    >
                      📑 Mehrere Bilder auswählen
                    </button>
                    {kontingentVerbleibend !== null && (
                      <span
                        className={`kontingent-anzeige grid-kontingent${
                          kontingentVerbleibend === 0
                            ? ' kontingent-aufgebraucht'
                            : ''
                        }`}
                      >
                        Noch {kontingentVerbleibend} von {dokumentKontingent}{' '}
                        Dokumenten
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {zeigeEinzelfotoThumbnail && (
              <EinzelfotoThumbnail file={angezeigteDatei} />
            )}

            <MultiImageCapture
              onImagesReady={handleImagesReady}
              onAbbrechen={() => setMultiPhotoMode(false)}
              disabled={erfassungGesperrt}
              open={multiPhotoMode}
              resetSignal={multiPhotoResetSignal}
              erzwingeKamera={kameraErzwingen}
            />

            {error && <div className='error-banner'>{error}</div>}

            <RechnungFieldsView
              rechnung={angezeigteRechnung}
              validation={angezeigteValidation}
              onRechnungChange={rechnungAendern}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RechnungTab
